// src/components/DiffTool.jsx
// Generic "paste JSON or a URL, diff it against something else" tool.
//
// Two exports:
//   - DiffToolCore  — the actual two-pane diff UI (textareas + result table), used both
//                      standalone (InspectPage's Compare tab) and inside a modal locked to a captured
//                      request (DiffToolModal).
//   - DiffToolModal — captures the left side from a request's url/requestBody/responseBody,
//                      right side is a free paste box.
//
// Diff engines are consumed via the pinned contracts in ../utils/jsonDiff and ../utils/urlDiff:
//   jsonDiff(left, right, opts) -> { same, truncated?, counts, entries: [{path, kind, left, right}] }
//   urlDiff(leftUrl, rightUrl)  -> { ok, reason?, same, origin, path, params, nested }
//
import React from "react";
import { jsonDiff } from "../utils/jsonDiff";
import { urlDiff } from "../utils/urlDiff";

/* ── clipboard helper — same approach as InspectraNetworkView's copyToClipboard,
   reimplemented locally per task instructions (do not import from InspectraNetworkView). ── */
function copyToClipboard(text) {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(String(text));
            return true;
        }
    } catch { /* fall through to legacy path */ }
    try {
        const ta = document.createElement("textarea");
        ta.value = String(text);
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        ta.remove();
        return true;
    } catch { return false; }
}

/* ── mode detection ── */

const looksLikeUrl = (s) => typeof s === "string" && /^https?:\/\//i.test(s.trim());

function tryParseJson(s) {
    if (typeof s !== "string") return { ok: false, err: "not a string" };
    const trimmed = s.trim();
    if (!trimmed) return { ok: false, err: "empty" };
    // An already-short string starting with http(s):// is NOT JSON, even if it
    // happens to parse-fail cleanly — short-circuit so URL mode wins.
    if (looksLikeUrl(trimmed) && trimmed.length < 2048 && !trimmed.startsWith("{") && !trimmed.startsWith("[")) {
        return { ok: false, err: "looks like a URL" };
    }
    try {
        return { ok: true, value: JSON.parse(trimmed) };
    } catch (e) {
        return { ok: false, err: e.message };
    }
}

/**
 * Decide the comparison mode from the two raw pasted/loaded strings.
 * Returns { mode: 'json'|'url'|'empty'|'error', leftJson, rightJson, error }
 */
function detectMode(leftRaw, rightRaw) {
    const l = (leftRaw ?? "").trim();
    const r = (rightRaw ?? "").trim();

    if (!l || !r) {
        return { mode: "empty" };
    }

    const lJson = tryParseJson(l);
    const rJson = tryParseJson(r);
    if (lJson.ok && rJson.ok) {
        return { mode: "json", leftJson: lJson.value, rightJson: rJson.value };
    }

    const lUrl = looksLikeUrl(l);
    const rUrl = looksLikeUrl(r);
    if (lUrl && rUrl) {
        return { mode: "url" };
    }

    // Mixed / unparseable — build a per-side hint.
    const sideHint = (raw, jsonRes, isUrl) => {
        if (isUrl) return "valid URL";
        if (jsonRes.ok) return "valid JSON";
        return `parse error at ${jsonRes.err}`;
    };
    const leftHint = sideHint(l, lJson, lUrl);
    const rightHint = sideHint(r, rJson, rUrl);
    return { mode: "error", error: `left: ${leftHint} · right: ${rightHint}` };
}

/* ── value formatting ── */

function stringifyCompact(v) {
    if (v === undefined) return "";
    if (v === null) return "null";
    if (typeof v === "string") return v;
    try { return JSON.stringify(v); } catch { return String(v); }
}

const MAX_CELL_LEN = 120;
function Cell({ value }) {
    const text = stringifyCompact(value);
    if (text.length <= MAX_CELL_LEN) return <span>{text || "—"}</span>;
    return <span title={text}>{text.slice(0, MAX_CELL_LEN)}…</span>;
}

const KIND_STYLE = {
    added: { background: "var(--success-bg, transparent)", color: "var(--success-text)" },
    removed: { background: "var(--error-bg, transparent)", color: "var(--error-text)" },
    changed: { background: "var(--warn-bg, transparent)", color: "var(--warn-text)" },
    same: { color: "var(--ink)" },
};

/* ── result renderers ── */

function JsonDiffResult({ result }) {
    if (!result) return null;
    const { same, truncated, counts, entries } = result;
    const total = (counts?.added || 0) + (counts?.removed || 0) + (counts?.changed || 0) + (counts?.same || 0);

    return (
        <div style={{ display: "grid", gap: 8 }}>
            <div style={s.summary}>
                {same
                    ? "identical"
                    : `${counts?.changed || 0} changed · ${counts?.added || 0} added · ${counts?.removed || 0} removed (of ${total} fields)`}
                {truncated && <span style={s.truncNote}> — truncated, showing partial diff</span>}
            </div>
            {!same && (
                <div style={{ overflowX: "auto" }}>
                    <table style={s.table}>
                        <thead>
                            <tr>
                                <th style={s.th}>Path</th>
                                <th style={s.th}>Left</th>
                                <th style={s.th}>Right</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(entries || []).map((e, i) => (
                                <tr key={e.path || i} style={KIND_STYLE[e.kind] || {}}>
                                    <td style={s.k} title={e.path || "(root)"}>{e.path || "(root)"}</td>
                                    <td style={s.v}><Cell value={e.left} /></td>
                                    <td style={s.v}><Cell value={e.right} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function ParamsTable({ rows, title }) {
    if (!rows || !rows.length) return null;
    return (
        <div style={{ display: "grid", gap: 4 }}>
            {title && <div style={s.subHeading}>{title}</div>}
            <div style={{ overflowX: "auto" }}>
                <table style={s.table}>
                    <thead>
                        <tr>
                            <th style={s.th}>Key</th>
                            <th style={s.th}>Left</th>
                            <th style={s.th}>Right</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={r.key || i} style={KIND_STYLE[r.kind] || {}}>
                                <td style={s.k} title={r.key}>{r.key}</td>
                                <td style={s.v}><Cell value={r.left} /></td>
                                <td style={s.v}><Cell value={r.right} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function UrlDiffResult({ result }) {
    if (!result) return null;
    if (result.ok === false) {
        return <div style={s.errorHint}>{result.reason || "Could not compare these URLs."}</div>;
    }

    const { same, origin, path, params, nested } = result;
    const nestedKeys = nested ? Object.keys(nested) : [];

    return (
        <div style={{ display: "grid", gap: 12 }}>
            <div style={s.summary}>{same ? "identical" : "URLs differ"}</div>

            <div style={{ overflowX: "auto" }}>
                <table style={s.table}>
                    <tbody>
                        <tr style={origin?.same ? {} : KIND_STYLE.changed}>
                            <td style={s.k}>Origin</td>
                            <td style={s.v}><Cell value={origin?.left} /></td>
                            <td style={s.v}><Cell value={origin?.right} /></td>
                        </tr>
                        <tr style={path?.same ? {} : KIND_STYLE.changed}>
                            <td style={s.k}>Path</td>
                            <td style={s.v}><Cell value={path?.left} /></td>
                            <td style={s.v}><Cell value={path?.right} /></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <ParamsTable rows={params} title="Query params" />

            {nestedKeys.map((key) => (
                <ParamsTable key={key} rows={nested[key]} title={`${key} (expanded)`} />
            ))}
        </div>
    );
}

/* ── main core component ── */

export function DiffToolCore({
    left,
    onLeftChange,
    right,
    onRightChange,
    leftLabel = "Left",
    rightLabel = "Right",
    leftLocked = false,
}) {
    const [copyFeedback, setCopyFeedback] = React.useState(null);

    const detection = React.useMemo(() => detectMode(left, right), [left, right]);

    const jsonResult = React.useMemo(() => {
        if (detection.mode !== "json") return null;
        try {
            return jsonDiff(detection.leftJson, detection.rightJson, {});
        } catch (e) {
            return { same: false, counts: { added: 0, removed: 0, changed: 0, same: 0 }, entries: [], error: e.message };
        }
    }, [detection]);

    const urlResult = React.useMemo(() => {
        if (detection.mode !== "url") return null;
        try {
            return urlDiff((left || "").trim(), (right || "").trim());
        } catch (e) {
            return { ok: false, reason: e.message };
        }
    }, [detection, left, right]);

    const doCopy = (text, label) => {
        const ok = copyToClipboard(text);
        setCopyFeedback(ok ? `${label} copied` : `${label} copy failed`);
        setTimeout(() => setCopyFeedback(null), 1500);
    };

    const handleCopyLeft = () => doCopy(left || "", "Left");
    const handleCopyRight = () => doCopy(right || "", "Right");
    const handleCopyDiff = () => {
        const payload = detection.mode === "json"
            ? jsonResult
            : detection.mode === "url"
                ? urlResult
                : null;
        doCopy(JSON.stringify(payload?.entries ?? payload ?? {}, null, 2), "Diff");
    };

    let hint = null;
    if (detection.mode === "empty") hint = "paste content to compare";
    if (detection.mode === "error") hint = detection.error;

    return (
        <div style={{ display: "grid", gap: 12 }}>
            <div style={s.panes}>
                <div style={{ display: "grid", gap: 6 }}>
                    <div style={s.paneLabel}>{leftLabel}</div>
                    <textarea
                        value={left ?? ""}
                        onChange={(e) => onLeftChange && onLeftChange(e.target.value)}
                        readOnly={leftLocked}
                        placeholder="Left side content…"
                        style={{ ...s.textarea, ...(leftLocked ? s.textareaReadOnly : {}) }}
                        spellCheck={false}
                    />
                </div>
                <div style={{ display: "grid", gap: 6 }}>
                    <div style={s.paneLabel}>{rightLabel}</div>
                    <textarea
                        value={right ?? ""}
                        onChange={(e) => onRightChange && onRightChange(e.target.value)}
                        placeholder="Paste JSON or a URL…"
                        style={s.textarea}
                        spellCheck={false}
                    />
                </div>
            </div>

            <div style={s.toolbar}>
                <button style={s.btn} onClick={handleCopyLeft}>Copy left</button>
                <button style={s.btn} onClick={handleCopyRight}>Copy right</button>
                <button
                    style={s.btn}
                    onClick={handleCopyDiff}
                    disabled={detection.mode !== "json" && detection.mode !== "url"}
                >
                    Copy diff
                </button>
                {copyFeedback && <span style={s.copyFeedback}>{copyFeedback}</span>}
            </div>

            {hint && <div style={s.hint}>{hint}</div>}
            {detection.mode === "json" && <JsonDiffResult result={jsonResult} />}
            {detection.mode === "url" && <UrlDiffResult result={urlResult} />}
        </div>
    );
}

/* ── modal wrapper — captures LEFT from a request, matches CompareRequestsModal's
   plain-overlay modal idiom (no MUI Dialog used elsewhere in this app). ── */

function bodyToText(v) {
    if (v == null) return "";
    if (typeof v === "string") return v;
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

export function DiffToolModal({ open, onClose, request }) {
    const sources = React.useMemo(() => {
        const list = [];
        if (request?.url) list.push({ key: "url", label: "URL", text: String(request.url) });
        const reqBodyText = bodyToText(request?.requestBody);
        if (reqBodyText) list.push({ key: "requestBody", label: "Request body", text: reqBodyText });
        const resBodyText = bodyToText(request?.responseBody);
        if (resBodyText) list.push({ key: "responseBody", label: "Response body", text: resBodyText });
        return list;
    }, [request]);

    const [source, setSource] = React.useState(sources[0]?.key || "url");
    const [right, setRight] = React.useState("");

    // Reset the selected source + paste box whenever a new request is loaded / modal reopened.
    React.useEffect(() => {
        if (!open) return;
        const firstAvailable = sources[0]?.key || "url";
        setSource((prev) => (sources.some((s0) => s0.key === prev) ? prev : firstAvailable));
    }, [open, sources]);

    if (!open) return null;

    const active = sources.find((s0) => s0.key === source) || sources[0];
    const leftValue = active?.text || "";

    return (
        <div style={m.overlay} onClick={onClose}>
            <div style={m.modal} onClick={(e) => e.stopPropagation()}>
                <div style={m.header}>
                    <strong>Diff vs pasted</strong>
                    <button style={m.btn} onClick={onClose}>Close</button>
                </div>

                <div style={{ padding: 12, display: "grid", gap: 12 }}>
                    <div style={m.sourceRow}>
                        <span style={m.sourceLabel}>Compare from:</span>
                        {["url", "requestBody", "responseBody"].map((key) => {
                            const def = { url: "URL", requestBody: "Request body", responseBody: "Response body" }[key];
                            const available = sources.some((s0) => s0.key === key);
                            return (
                                <label key={key} style={{ ...m.radioLabel, ...(available ? {} : m.radioLabelDisabled) }}>
                                    <input
                                        type="radio"
                                        name="diff-source"
                                        checked={source === key}
                                        disabled={!available}
                                        onChange={() => setSource(key)}
                                    />
                                    <span>{def}</span>
                                </label>
                            );
                        })}
                    </div>

                    <DiffToolCore
                        left={leftValue}
                        onLeftChange={() => {}}
                        right={right}
                        onRightChange={setRight}
                        leftLabel={active?.label || "Left"}
                        rightLabel="Paste to compare"
                        leftLocked
                    />
                </div>
            </div>
        </div>
    );
}

/* ── styles ── */

const s = {
    panes: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
    paneLabel: { fontWeight: 600, fontSize: 12, color: "var(--ink)" },
    textarea: {
        minHeight: 220,
        resize: "vertical",
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        lineHeight: 1.4,
        padding: 10,
        border: "1px solid var(--line)",
        borderRadius: 8,
        background: "var(--bg)",
        color: "var(--ink)",
        whiteSpace: "pre",
        overflowWrap: "normal",
    },
    textareaReadOnly: { background: "var(--card)", color: "var(--muted)" },
    toolbar: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
    btn: {
        padding: "6px 10px", fontSize: 12, border: "1px solid var(--line)",
        borderRadius: 8, background: "var(--card)", cursor: "pointer", color: "var(--ink)",
    },
    copyFeedback: { fontSize: 12, color: "var(--muted)" },
    hint: { fontSize: 12, color: "var(--muted)", fontStyle: "italic" },
    errorHint: { fontSize: 12, color: "var(--error-text)" },
    summary: { fontWeight: 600, fontSize: 13, color: "var(--ink)" },
    truncNote: { fontWeight: 400, color: "var(--warn-text)" },
    subHeading: { fontWeight: 600, fontSize: 12, color: "var(--ink)", marginTop: 4 },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 12, color: "var(--ink)" },
    th: { textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--line)", color: "var(--muted)" },
    k: {
        width: 260, padding: "6px 8px", borderBottom: "1px solid var(--line)",
        verticalAlign: "top", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere",
    },
    v: { padding: "6px 8px", borderBottom: "1px solid var(--line)", verticalAlign: "top", wordBreak: "break-word" },
};

const m = {
    overlay: {
        position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    },
    modal: {
        width: "min(1200px, 98vw)", maxHeight: "92vh", overflow: "auto",
        background: "var(--card)", border: "1px solid var(--line)", borderRadius: 12,
        boxShadow: "0 10px 30px rgba(0,0,0,.25)", color: "var(--ink)",
    },
    header: {
        padding: "10px 12px", borderBottom: "1px solid var(--line)", background: "var(--bg)",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
        color: "var(--ink)",
    },
    btn: {
        padding: "6px 10px", fontSize: 12, border: "1px solid var(--line)",
        borderRadius: 8, background: "var(--card)", cursor: "pointer", color: "var(--ink)",
    },
    sourceRow: { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" },
    sourceLabel: { fontSize: 12, color: "var(--muted)", fontWeight: 600 },
    radioLabel: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--ink)", cursor: "pointer" },
    radioLabelDisabled: { color: "var(--muted)", cursor: "not-allowed", opacity: 0.6 },
};
